;globalThis["_sentryRewritesTunnelPath"] = "/monitoring-tunnel";globalThis["SENTRY_RELEASE"] = {"id":"e159eb81be5cd6f72fc83250c22d425e4e48600d"};globalThis["_sentryBasePath"] = undefined;globalThis["_sentryNextJsVersion"] = "16.2.3";globalThis["_sentryRewriteFramesAssetPrefixPath"] = "";globalThis["_sentryAssetPrefix"] = undefined;globalThis["_sentryExperimentalThirdPartyOriginStackFrames"] = undefined;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/invitations/:invitationId\",\"regex\":\"^/invitations/([^/]+)$\",\"paramNames\":[\"invitationId\"],\"hasOptionalPrefix\":false},{\"path\":\"/invitations/:invitationId/gallery\",\"regex\":\"^/invitations/([^/]+)/gallery$\",\"paramNames\":[\"invitationId\"],\"hasOptionalPrefix\":false},{\"path\":\"/invitations/:invitationId/guestbook\",\"regex\":\"^/invitations/([^/]+)/guestbook$\",\"paramNames\":[\"invitationId\"],\"hasOptionalPrefix\":false},{\"path\":\"/invitations/:invitationId/rsvps\",\"regex\":\"^/invitations/([^/]+)/rsvps$\",\"paramNames\":[\"invitationId\"],\"hasOptionalPrefix\":false},{\"path\":\"/keepsakes/:categorySlug\",\"regex\":\"^/keepsakes/([^/]+)$\",\"paramNames\":[\"categorySlug\"],\"hasOptionalPrefix\":false},{\"path\":\"/keepsakes/:categorySlug/:sku\",\"regex\":\"^/keepsakes/([^/]+)/([^/]+)$\",\"paramNames\":[\"categorySlug\",\"sku\"],\"hasOptionalPrefix\":false},{\"path\":\"/keepsakes/stationery/invitation/:invitationId\",\"regex\":\"^/keepsakes/stationery/invitation/([^/]+)$\",\"paramNames\":[\"invitationId\"],\"hasOptionalPrefix\":false},{\"path\":\"/orders/:orderId\",\"regex\":\"^/orders/([^/]+)$\",\"paramNames\":[\"orderId\"],\"hasOptionalPrefix\":false},{\"path\":\"/stationery-invitations/:stationeryId\",\"regex\":\"^/stationery-invitations/([^/]+)$\",\"paramNames\":[\"stationeryId\"],\"hasOptionalPrefix\":false},{\"path\":\"/designs/demo/:heroId\",\"regex\":\"^/designs/demo/([^/]+)$\",\"paramNames\":[\"heroId\"],\"hasOptionalPrefix\":false}],\"staticRoutes\":[{\"path\":\"/\"},{\"path\":\"/account\"},{\"path\":\"/export-callback\"},{\"path\":\"/invitations\"},{\"path\":\"/keepsakes\"},{\"path\":\"/notifications\"},{\"path\":\"/oauth-start\"},{\"path\":\"/orders\"},{\"path\":\"/settings\"},{\"path\":\"/builder\"},{\"path\":\"/cart\"},{\"path\":\"/checkout\"},{\"path\":\"/checkout/result\"},{\"path\":\"/cookies\"},{\"path\":\"/designs\"},{\"path\":\"/login\"},{\"path\":\"/privacy\"},{\"path\":\"/refund-policy\"},{\"path\":\"/sms-policy\"},{\"path\":\"/stationery-builder\"},{\"path\":\"/terms\"}],\"isrRoutes\":[\"/designs/demo/:heroId\"]}";type SentryModule = typeof import('@sentry/nextjs')

const SENTRY_DSN = process.env.NEXT_PUBLIC_SENTRY_DSN
const ENV = process.env.NEXT_PUBLIC_ENV || 'development'
const IS_PRODUCTION = ENV === 'production'
const IS_STAGING = ENV === 'staging'
const IS_DEV = process.env.NODE_ENV === 'development'
const SENTRY_REVEAL_EVENTS = [
  'pointerdown',
  'keydown',
  'scroll',
  'touchstart',
] as const

let sentryModule: SentryModule | null = null
let sentryModulePromise: Promise<SentryModule | null> | null = null
let sentryInitialized = false

async function loadSentryModule(): Promise<SentryModule | null> {
  if (!SENTRY_DSN || typeof window === 'undefined') return null
  if (sentryModule) return sentryModule
  if (sentryModulePromise) return sentryModulePromise

  sentryModulePromise = import('@sentry/nextjs')
    .then((mod) => {
      sentryModule = mod
      return mod
    })
    .catch((error) => {
      console.warn('[Sentry] Failed to load client SDK:', error)
      sentryModulePromise = null
      return null
    })

  return sentryModulePromise
}

export function onRouterTransitionStart(
  ...args: Parameters<SentryModule['captureRouterTransitionStart']>
) {
  sentryModule?.captureRouterTransitionStart(...args)
}

// Sensitive field patterns to redact from Sentry events
// Be specific to avoid redacting legitimate debug context
const SENSITIVE_PATTERNS = [
  /password/i,
  /^secret$/i,
  /secret[_-]?key/i,
  /^token$/i,
  /access[_-]?token/i,
  /refresh[_-]?token/i,
  /api[_-]?key/i,
  /auth[_-]?token/i,
  /authorization/i,
  /credential/i,
  /credit[_-]?card/i,
  /card[_-]?number/i,
  /^cvv$/i,
  /^cvc$/i,
  /^ssn$/i,
  /social[_-]?security/i,
  /^phone$/i, // Only exact "phone" field, not phoneType etc.
  /phone[_-]?number/i,
  /^email$/i, // Only exact "email" field, not emailSent etc.
  /email[_-]?address/i,
]

/**
 * Recursively sanitize sensitive data from objects before sending to Sentry.
 */
function sanitizeSensitiveData<T>(obj: T, depth = 0): T {
  if (depth > 10) return obj
  if (obj === null || obj === undefined) return obj
  if (typeof obj !== 'object') return obj

  if (Array.isArray(obj)) {
    return obj.map((item) => sanitizeSensitiveData(item, depth + 1)) as T
  }

  const sanitized: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    const isSensitive = SENSITIVE_PATTERNS.some((pattern) => pattern.test(key))
    if (isSensitive && value) {
      sanitized[key] = '[REDACTED]'
    } else if (typeof value === 'object') {
      sanitized[key] = sanitizeSensitiveData(value, depth + 1)
    } else {
      sanitized[key] = value
    }
  }

  return sanitized as T
}

async function initSentryClient() {
  if (sentryInitialized) return
  sentryInitialized = true

  const Sentry = await loadSentryModule()
  if (!Sentry) return

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENV,

    // Performance monitoring: Sample rates
    // Production: 10%, Staging: 100%, Dev: disabled
    tracesSampleRate: IS_PRODUCTION ? 0.1 : IS_STAGING ? 1.0 : 0,

    // Session Replay: Capture user sessions for debugging
    // Production: 10% of sessions, 100% of sessions with errors
    // Staging: 100% of sessions with errors only
    replaysSessionSampleRate: IS_PRODUCTION ? 0.1 : 0,
    replaysOnErrorSampleRate: IS_PRODUCTION || IS_STAGING ? 1.0 : 0,

    integrations: [
      Sentry.browserTracingIntegration({
        enableLongTask: true,
      }),
      ...(IS_PRODUCTION || IS_STAGING
        ? [
            Sentry.replayIntegration({
              maskAllText: IS_PRODUCTION,
              blockAllMedia: IS_PRODUCTION,
            }),
          ]
        : []),
    ],

    ignoreErrors: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-extension:\/\//i,
      'Network Error',
      'Failed to fetch',
      'Load failed',
      'AbortError',
      'NetworkError',
      'TypeError: Failed to fetch',
      'TypeError: NetworkError',
      'TypeError: cancelled',
      'cancelled',
      'canceled',
      'AbortError: The operation was aborted',
      /googletagmanager/i,
      /facebook/i,
      /analytics/i,
      /hotjar/i,
      /intercom/i,
      'ResizeObserver loop',
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection',
      'Non-Error exception captured',
      'Hydration failed',
      'Text content does not match',
    ],

    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-extension:\/\//i,
      /googletagmanager\.com/i,
      /google-analytics\.com/i,
      /facebook\.net/i,
      /connect\.facebook\.net/i,
      /hotjar\.com/i,
      /intercom\.io/i,
    ],

    enabled: !IS_DEV,
    profilesSampleRate: IS_PRODUCTION ? 0.1 : IS_STAGING ? 1.0 : 0,

    beforeSend(event) {
      if (typeof window !== 'undefined') {
        const hostname = window.location.hostname
        if (hostname === 'localhost' || hostname === '127.0.0.1') {
          return null
        }
      }

      if (event.extra) {
        event.extra = sanitizeSensitiveData(event.extra)
      }
      if (event.contexts) {
        event.contexts = sanitizeSensitiveData(event.contexts)
      }
      if (event.request?.data) {
        event.request.data = sanitizeSensitiveData(event.request.data)
      }

      event.tags = {
        ...event.tags,
        component: 'client',
        environment: ENV,
      }

      return event
    },

    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === 'console' && breadcrumb.level === 'log') {
        return null
      }
      return breadcrumb
    },
  })

  if (!IS_PRODUCTION) {
    ;(window as typeof window & { Sentry?: SentryModule }).Sentry = Sentry
  }
}

if (SENTRY_DSN && typeof window !== 'undefined') {
  let scheduled = false

  const scheduleInit = () => {
    if (scheduled) return
    scheduled = true
    cleanup()

    if ('requestIdleCallback' in window) {
      requestIdleCallback(
        () => {
          void initSentryClient()
        },
        { timeout: 3000 }
      )
    } else {
      setTimeout(() => {
        void initSentryClient()
      }, 3000)
    }
  }

  const hardDeadline = setTimeout(scheduleInit, 5000)

  const cleanup = () => {
    clearTimeout(hardDeadline)
    SENTRY_REVEAL_EVENTS.forEach((eventName) =>
      window.removeEventListener(eventName, scheduleInit, true)
    )
  }

  SENTRY_REVEAL_EVENTS.forEach((eventName) =>
    window.addEventListener(eventName, scheduleInit, {
      once: true,
      capture: true,
      passive: true,
    })
  )
}
